import { LatexNode, AtomType } from './types';
import { DRAWN_SYMBOLS } from './symbols';

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

type TokenKind =
  | 'CHAR'
  | 'COMMAND'
  | 'LBRACE'
  | 'RBRACE'
  | 'CARET'
  | 'UNDERSCORE'
  | 'LPAR'
  | 'RPAR'
  | 'LBRACKET'
  | 'RBRACKET'
  | 'AMPERSAND'
  | 'EOF';

interface Token {
  kind: TokenKind;
  value: string;
}

// ---------------------------------------------------------------------------
// Font-glyph command table (characters that ARE in NotoSans)
// ---------------------------------------------------------------------------

const FONT_GLYPHS: Record<string, { ch: string; atomType: AtomType }> = {
  // Greek lowercase
  alpha: { ch: 'α', atomType: 'ord' },
  beta: { ch: 'β', atomType: 'ord' },
  gamma: { ch: 'γ', atomType: 'ord' },
  delta: { ch: 'δ', atomType: 'ord' },
  epsilon: { ch: 'ε', atomType: 'ord' },
  varepsilon: { ch: 'ε', atomType: 'ord' },
  zeta: { ch: 'ζ', atomType: 'ord' },
  eta: { ch: 'η', atomType: 'ord' },
  theta: { ch: 'θ', atomType: 'ord' },
  vartheta: { ch: 'ϑ', atomType: 'ord' },
  iota: { ch: 'ι', atomType: 'ord' },
  kappa: { ch: 'κ', atomType: 'ord' },
  lambda: { ch: 'λ', atomType: 'ord' },
  mu: { ch: 'μ', atomType: 'ord' },
  nu: { ch: 'ν', atomType: 'ord' },
  xi: { ch: 'ξ', atomType: 'ord' },
  pi: { ch: 'π', atomType: 'ord' },
  varpi: { ch: 'ϖ', atomType: 'ord' },
  rho: { ch: 'ρ', atomType: 'ord' },
  varrho: { ch: 'ϱ', atomType: 'ord' },
  sigma: { ch: 'σ', atomType: 'ord' },
  varsigma: { ch: 'ς', atomType: 'ord' },
  tau: { ch: 'τ', atomType: 'ord' },
  upsilon: { ch: 'υ', atomType: 'ord' },
  phi: { ch: 'φ', atomType: 'ord' },
  varphi: { ch: 'ϕ', atomType: 'ord' },
  chi: { ch: 'χ', atomType: 'ord' },
  psi: { ch: 'ψ', atomType: 'ord' },
  omega: { ch: 'ω', atomType: 'ord' },
  // Greek uppercase
  Gamma: { ch: 'Γ', atomType: 'ord' },
  Delta: { ch: 'Δ', atomType: 'ord' },
  Theta: { ch: 'Θ', atomType: 'ord' },
  Lambda: { ch: 'Λ', atomType: 'ord' },
  Xi: { ch: 'Ξ', atomType: 'ord' },
  Pi: { ch: 'Π', atomType: 'ord' },
  Sigma: { ch: 'Σ', atomType: 'ord' },
  Upsilon: { ch: 'Υ', atomType: 'ord' },
  Phi: { ch: 'Φ', atomType: 'ord' },
  Psi: { ch: 'Ψ', atomType: 'ord' },
  Omega: { ch: 'Ω', atomType: 'ord' },
  // Latin-1 operators present in NotoSans
  times: { ch: '×', atomType: 'bin' },
  div: { ch: '÷', atomType: 'bin' },
  pm: { ch: '±', atomType: 'bin' },
  cdot: { ch: '·', atomType: 'bin' },
  neg: { ch: '¬', atomType: 'ord' },
  lnot: { ch: '¬', atomType: 'ord' },
  // Dashes / punctuation in NotoSans
  minus: { ch: '−', atomType: 'bin' },
  endash: { ch: '–', atomType: 'ord' },
  emdash: { ch: '—', atomType: 'ord' },
  textendash: { ch: '–', atomType: 'ord' },
  textemdash: { ch: '—', atomType: 'ord' },
  // Primes
  prime: { ch: '′', atomType: 'ord' },
  dprime: { ch: '″', atomType: 'ord' },
  // Number sets (letterlike — all in NotoSans)
  mathbb_R: { ch: 'ℝ', atomType: 'ord' },
  mathbb_N: { ch: 'ℕ', atomType: 'ord' },
  mathbb_Z: { ch: 'ℤ', atomType: 'ord' },
  mathbb_Q: { ch: 'ℚ', atomType: 'ord' },
  mathbb_C: { ch: 'ℂ', atomType: 'ord' },
  // Other letterlike
  aleph: { ch: 'ℵ', atomType: 'ord' },
  hbar: { ch: 'ℏ', atomType: 'ord' },
  ell: { ch: 'ℓ', atomType: 'ord' },
  wp: { ch: '℘', atomType: 'ord' },
  Re: { ch: 'ℜ', atomType: 'ord' },
  Im: { ch: 'ℑ', atomType: 'ord' },
  // Misc Latin-1 in NotoSans
  deg: { ch: '°', atomType: 'ord' },
  dagger: { ch: '†', atomType: 'bin' },
  ddagger: { ch: '‡', atomType: 'bin' },
  S: { ch: '§', atomType: 'ord' },
  P: { ch: '¶', atomType: 'ord' },
  copyright: { ch: '©', atomType: 'ord' },
  trademark: { ch: '™', atomType: 'ord' },
  registered: { ch: '®', atomType: 'ord' },
  // Escaped braces
  lbrace: { ch: '{', atomType: 'open' },
  rbrace: { ch: '}', atomType: 'close' },
};

// Drawn-symbol command table (SVG-rendered, not in NotoSans)
const DRAWN_CMDS: Record<string, { name: string; atomType: AtomType }> = {
  // Relations
  leq: { name: 'leq', atomType: 'rel' },
  le: { name: 'leq', atomType: 'rel' },
  geq: { name: 'geq', atomType: 'rel' },
  ge: { name: 'geq', atomType: 'rel' },
  neq: { name: 'neq', atomType: 'rel' },
  ne: { name: 'neq', atomType: 'rel' },
  approx: { name: 'approx', atomType: 'rel' },
  equiv: { name: 'equiv', atomType: 'rel' },
  sim: { name: 'sim', atomType: 'rel' },
  simeq: { name: 'simeq', atomType: 'rel' },
  cong: { name: 'cong', atomType: 'rel' },
  propto: { name: 'propto', atomType: 'rel' },
  ll: { name: 'll', atomType: 'rel' },
  gg: { name: 'gg', atomType: 'rel' },
  prec: { name: 'prec', atomType: 'rel' },
  succ: { name: 'succ', atomType: 'rel' },
  // Negated relations
  nleq: { name: 'nleq', atomType: 'rel' },
  ngeq: { name: 'ngeq', atomType: 'rel' },
  nless: { name: 'nless', atomType: 'rel' },
  ngtr: { name: 'ngtr', atomType: 'rel' },
  nsim: { name: 'nsim', atomType: 'rel' },
  napprox: { name: 'napprox', atomType: 'rel' },
  ncong: { name: 'ncong', atomType: 'rel' },
  nequiv: { name: 'neqv', atomType: 'rel' },
  // Arrows
  rightarrow: { name: 'rightarrow', atomType: 'rel' },
  leftarrow: { name: 'leftarrow', atomType: 'rel' },
  leftrightarrow: { name: 'leftrightarrow', atomType: 'rel' },
  uparrow: { name: 'uparrow', atomType: 'rel' },
  downarrow: { name: 'downarrow', atomType: 'rel' },
  updownarrow: { name: 'updownarrow', atomType: 'rel' },
  Rightarrow: { name: 'Rightarrow', atomType: 'rel' },
  Leftarrow: { name: 'Leftarrow', atomType: 'rel' },
  Leftrightarrow: { name: 'Leftrightarrow', atomType: 'rel' },
  Uparrow: { name: 'Uparrow', atomType: 'rel' },
  Downarrow: { name: 'Downarrow', atomType: 'rel' },
  nearrow: { name: 'nearrow', atomType: 'rel' },
  searrow: { name: 'searrow', atomType: 'rel' },
  swarrow: { name: 'swarrow', atomType: 'rel' },
  nwarrow: { name: 'nwarrow', atomType: 'rel' },
  to: { name: 'rightarrow', atomType: 'rel' },
  gets: { name: 'leftarrow', atomType: 'rel' },
  implies: { name: 'Rightarrow', atomType: 'rel' },
  iff: { name: 'Leftrightarrow', atomType: 'rel' },
  longrightarrow: { name: 'longrightarrow', atomType: 'rel' },
  longleftarrow: { name: 'longleftarrow', atomType: 'rel' },
  longleftrightarrow: { name: 'longleftrightarrow', atomType: 'rel' },
  Longrightarrow: { name: 'Longrightarrow', atomType: 'rel' },
  Longleftarrow: { name: 'Longleftarrow', atomType: 'rel' },
  Longleftrightarrow: { name: 'Longleftrightarrow', atomType: 'rel' },
  mapsto: { name: 'mapsto', atomType: 'rel' },
  hookrightarrow: { name: 'hookrightarrow', atomType: 'rel' },
  hookleftarrow: { name: 'hookleftarrow', atomType: 'rel' },
  rightharpoonup: { name: 'rightharpoonup', atomType: 'rel' },
  rightharpoondown: { name: 'rightharpoondown', atomType: 'rel' },
  leftharpoonup: { name: 'leftharpoonup', atomType: 'rel' },
  rightleftharpoons: { name: 'rightleftharpoons', atomType: 'rel' },
  // Set theory
  in: { name: 'in', atomType: 'rel' },
  notin: { name: 'notin', atomType: 'rel' },
  ni: { name: 'ni', atomType: 'rel' },
  subset: { name: 'subset', atomType: 'rel' },
  supset: { name: 'supset', atomType: 'rel' },
  subseteq: { name: 'subseteq', atomType: 'rel' },
  supseteq: { name: 'supseteq', atomType: 'rel' },
  cap: { name: 'cap', atomType: 'bin' },
  cup: { name: 'cup', atomType: 'bin' },
  emptyset: { name: 'emptyset', atomType: 'ord' },
  varnothing: { name: 'emptyset', atomType: 'ord' },
  setminus: { name: 'setminus', atomType: 'bin' },
  // Logic
  forall: { name: 'forall', atomType: 'ord' },
  exists: { name: 'exists', atomType: 'ord' },
  nexists: { name: 'nexists', atomType: 'ord' },
  wedge: { name: 'wedge', atomType: 'bin' },
  land: { name: 'wedge', atomType: 'bin' },
  vee: { name: 'vee', atomType: 'bin' },
  lor: { name: 'vee', atomType: 'bin' },
  top: { name: 'top', atomType: 'ord' },
  perp: { name: 'perp', atomType: 'ord' },
  bot: { name: 'bot', atomType: 'ord' },
  mp: { name: 'mp', atomType: 'bin' },
  // Misc
  infty: { name: 'infty', atomType: 'ord' },
  partial: { name: 'partial', atomType: 'ord' },
  nabla: { name: 'nabla', atomType: 'ord' },
  oplus: { name: 'oplus', atomType: 'bin' },
  ominus: { name: 'ominus', atomType: 'bin' },
  otimes: { name: 'otimes', atomType: 'bin' },
  odot: { name: 'odot', atomType: 'bin' },
  parallel: { name: 'parallel', atomType: 'rel' },
  angle: { name: 'angle', atomType: 'ord' },
  therefore: { name: 'therefore', atomType: 'rel' },
  because: { name: 'because', atomType: 'rel' },
  cdots: { name: 'cdots', atomType: 'inner' },
  vdots: { name: 'vdots', atomType: 'inner' },
  ddots: { name: 'ddots', atomType: 'inner' },
  // Large operators
  sum: { name: 'sum', atomType: 'op' },
  prod: { name: 'prod', atomType: 'op' },
  coprod: { name: 'coprod', atomType: 'op' },
  int: { name: 'int', atomType: 'op' },
  oint: { name: 'oint', atomType: 'op' },
  iint: { name: 'iint', atomType: 'op' },
  bigcap: { name: 'cap', atomType: 'op' },
  bigcup: { name: 'cup', atomType: 'op' },
  bigoplus: { name: 'oplus', atomType: 'op' },
  // Delimiters (as standalone symbols)
  lfloor: { name: 'lfloor', atomType: 'open' },
  rfloor: { name: 'rfloor', atomType: 'close' },
  lceil: { name: 'lceil', atomType: 'open' },
  rceil: { name: 'rceil', atomType: 'close' },
  langle: { name: 'langle', atomType: 'open' },
  rangle: { name: 'rangle', atomType: 'close' },
  // Bra-ket notation
  lvert: { name: 'lvert', atomType: 'ord' },
  rvert: { name: 'rvert', atomType: 'ord' },
  lVert: { name: 'lVert', atomType: 'ord' },
  rVert: { name: 'rVert', atomType: 'ord' },
  // Operator symbols
  triangle: { name: 'triangle', atomType: 'ord' },
  square: { name: 'square', atomType: 'ord' },
  checkmark: { name: 'checkmark', atomType: 'ord' },
  bullet: { name: 'bullet', atomType: 'bin' },
};

// Built-in functions (operator style — rendered upright as 'op')
const OP_FUNCTIONS = new Set([
  'sin',
  'cos',
  'tan',
  'cot',
  'sec',
  'csc',
  'arcsin',
  'arccos',
  'arctan',
  'arccot',
  'sinh',
  'cosh',
  'tanh',
  'coth',
  'log',
  'ln',
  'lg',
  'exp',
  'lim',
  'limsup',
  'liminf',
  'max',
  'min',
  'sup',
  'inf',
  'det',
  'dim',
  'ker',
  'gcd',
  'lcm',
  'deg',
  'arg',
  'Pr',
  'hom',
  'Re',
  'Im',
]);

// \mathbb shorthand for common sets
const MATHBB: Record<string, string> = {
  R: 'ℝ',
  N: 'ℕ',
  Z: 'ℤ',
  Q: 'ℚ',
  C: 'ℂ',
  P: 'ℙ',
  F: '𝔽',
  H: 'ℍ',
};

// Font size multipliers
const SIZES: Record<string, number> = {
  tiny: 0.5,
  scriptsize: 0.7,
  footnotesize: 0.8,
  small: 0.9,
  normalsize: 1.0,
  large: 1.2,
  Large: 1.44,
  LARGE: 1.728,
  huge: 2.07,
  Huge: 2.49,
};

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < input.length) {
    const ch = input[i];
    if (ch === '\\') {
      i++;
      if (i >= input.length) break;
      const next = input[i];
      if (/[a-zA-Z]/.test(next)) {
        let word = '';
        while (i < input.length && /[a-zA-Z]/.test(input[i])) word += input[i++];
        tokens.push({ kind: 'COMMAND', value: word });
      } else {
        tokens.push({ kind: 'COMMAND', value: next });
        i++;
      }
    } else if (ch === '{') {
      tokens.push({ kind: 'LBRACE', value: '{' });
      i++;
    } else if (ch === '}') {
      tokens.push({ kind: 'RBRACE', value: '}' });
      i++;
    } else if (ch === '^') {
      tokens.push({ kind: 'CARET', value: '^' });
      i++;
    } else if (ch === '_') {
      tokens.push({ kind: 'UNDERSCORE', value: '_' });
      i++;
    } else if (ch === '(') {
      tokens.push({ kind: 'LPAR', value: '(' });
      i++;
    } else if (ch === ')') {
      tokens.push({ kind: 'RPAR', value: ')' });
      i++;
    } else if (ch === '[') {
      tokens.push({ kind: 'LBRACKET', value: '[' });
      i++;
    } else if (ch === ']') {
      tokens.push({ kind: 'RBRACKET', value: ']' });
      i++;
    } else if (ch === '&') {
      tokens.push({ kind: 'AMPERSAND', value: '&' });
      i++;
    } else if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i++;
    } else {
      tokens.push({ kind: 'CHAR', value: ch });
      i++;
    }
  }
  tokens.push({ kind: 'EOF', value: '' });
  return tokens;
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

class Parser {
  private pos = 0;
  constructor(private readonly tokens: Token[]) {}

  private peek(): Token {
    return this.tokens[this.pos];
  }
  private consume(): Token {
    return this.tokens[this.pos++];
  }
  private expect(kind: TokenKind): Token {
    const t = this.consume();
    if (t.kind !== kind) throw new Error(`Expected ${kind}, got ${t.kind}`);
    return t;
  }

  // ---------------------------------------------------------------------------
  // expr := item* (stops at }, ], \right, &, \\)
  // ---------------------------------------------------------------------------
  parseExpr(): LatexNode {
    const items: LatexNode[] = [];
    while (true) {
      const t = this.peek();
      if (t.kind === 'EOF' || t.kind === 'RBRACE' || t.kind === 'RBRACKET') break;
      if (t.kind === 'AMPERSAND') break;
      if (t.kind === 'COMMAND' && (t.value === 'right' || t.value === 'end' || t.value === '\\'))
        break;
      items.push(this.parseItem());
    }
    if (items.length === 1) return items[0];
    return { type: 'row', items };
  }

  // item := scriptable (^/_)*
  private parseItem(): LatexNode {
    const base = this.parseScriptable();
    let sup: LatexNode | undefined;
    let sub: LatexNode | undefined;
    let limits = false;

    // Check for limits modifier
    if (this.peek().kind === 'COMMAND' && this.peek().value === 'limits') {
      this.consume();
      limits = true;
    } else if (this.peek().kind === 'COMMAND' && this.peek().value === 'nolimits') {
      this.consume();
    }

    const t1 = this.peek();
    if (t1.kind === 'CARET') {
      this.consume();
      sup = this.parseGroup();
      if (this.peek().kind === 'UNDERSCORE') {
        this.consume();
        sub = this.parseGroup();
      }
    } else if (t1.kind === 'UNDERSCORE') {
      this.consume();
      sub = this.parseGroup();
      if (this.peek().kind === 'CARET') {
        this.consume();
        sup = this.parseGroup();
      }
    }

    if (sup !== undefined || sub !== undefined) {
      return { type: 'script', base, sup, sub, limits };
    }
    return base;
  }

  // group := { expr } | scriptable
  private parseGroup(): LatexNode {
    if (this.peek().kind === 'LBRACE') {
      this.consume();
      const inner = this.parseExpr();
      if (this.peek().kind === 'RBRACE') this.consume();
      return inner;
    }
    return this.parseScriptable();
  }

  private parseScriptable(): LatexNode {
    const t = this.peek();
    if (t.kind === 'LBRACE') {
      this.consume();
      const inner = this.parseExpr();
      if (this.peek().kind === 'RBRACE') this.consume();
      return inner;
    }
    if (t.kind === 'LPAR') {
      this.consume();
      // Bare ( is just a character — only \left( auto-scales
      return { type: 'char', ch: '(', italic: false, atomType: 'open' };
    }
    if (t.kind === 'RPAR') {
      this.consume();
      return { type: 'char', ch: ')', italic: false, atomType: 'close' };
    }
    if (t.kind === 'LBRACKET') {
      this.consume();
      const body = this.parseExpr();
      if (this.peek().kind === 'RBRACKET') this.consume();
      return { type: 'delimited', open: '[', close: ']', body };
    }
    if (t.kind === 'COMMAND') return this.parseCommand();
    if (t.kind === 'CHAR') {
      this.consume();
      const ch = t.value;
      // Atom type classification
      let atomType: AtomType = 'ord';
      if (ch === '+' || ch === '*') atomType = 'bin';
      else if (ch === '-')
        atomType = 'bin'; // in math context
      else if (ch === '=' || ch === '<' || ch === '>') atomType = 'rel';
      else if (ch === ',' || ch === ';') atomType = 'punct';
      else if (ch === '(' || ch === '[') atomType = 'open';
      else if (ch === ')' || ch === ']') atomType = 'close';
      const italic = /^[a-zA-Z]$/.test(ch);
      // Use proper Unicode minus sign (U+2212) instead of ASCII hyphen-minus
      const actualCh = ch === '-' ? '\u2212' : ch;
      return { type: 'char', ch: actualCh, italic, atomType };
    }
    this.consume();
    return { type: 'space', em: 0 };
  }

  // ---------------------------------------------------------------------------
  // Commands
  // ---------------------------------------------------------------------------
  private parseCommand(): LatexNode {
    const t = this.consume();
    const cmd = t.value;

    // Font size commands
    if (cmd in SIZES) {
      const sizeMul = SIZES[cmd];
      // Parse the rest of the current group as the sized body
      // For standalone size commands like {\large ...}, parse expr
      const body = this.parseExpr();
      return { type: 'sized', body, sizeMul };
    }

    // Built-in functions
    if (OP_FUNCTIONS.has(cmd)) {
      return { type: 'op_func', name: cmd };
    }

    // Structural commands
    switch (cmd) {
      case 'frac': {
        const num = this.parseGroup();
        const den = this.parseGroup();
        return { type: 'frac', num, den };
      }
      case 'sqrt': {
        let index: LatexNode | undefined;
        if (this.peek().kind === 'LBRACKET') {
          this.consume();
          index = this.parseExpr();
          if (this.peek().kind === 'RBRACKET') this.consume();
        }
        return { type: 'sqrt', body: this.parseGroup(), index };
      }
      case 'left': {
        const open = this.parseDelimChar();
        const body = this.parseExpr();
        let close = mirrorDelim(open);
        if (this.peek().kind === 'COMMAND' && this.peek().value === 'right') {
          this.consume();
          close = this.parseDelimChar(); // use the ACTUAL \right delimiter (e.g. '.' for \right.)
        }
        return { type: 'delimited', open, close, body };
      }
      case 'text':
      case 'mathrm':
      case 'mathit':
      case 'mathbf':
      case 'textrm':
      case 'textbf': {
        this.expect('LBRACE');
        let content = '';
        while (this.peek().kind !== 'RBRACE' && this.peek().kind !== 'EOF') {
          const tok = this.consume();
          content += tok.value;
        }
        if (this.peek().kind === 'RBRACE') this.consume();
        return { type: 'text', content };
      }
      case 'binom': {
        const top = this.parseGroup();
        const bot = this.parseGroup();
        // Render as fraction-like: (top over bot) in parens
        const fracNode: LatexNode = { type: 'frac', num: top, den: bot };
        return { type: 'delimited', open: '(', close: ')', body: fracNode };
      }
      case 'overline':
        return { type: 'overline', body: this.parseGroup() };
      case 'underline':
        return { type: 'underline', body: this.parseGroup() };
      case 'overbrace': {
        const body = this.parseGroup();
        let label: LatexNode | undefined;
        if (this.peek().kind === 'CARET') {
          this.consume();
          label = this.parseGroup();
        }
        return { type: 'overbrace', body, label };
      }
      case 'underbrace': {
        const body = this.parseGroup();
        let label: LatexNode | undefined;
        if (this.peek().kind === 'UNDERSCORE') {
          this.consume();
          label = this.parseGroup();
        }
        return { type: 'underbrace', body, label };
      }
      // Accents
      case 'hat':
      case 'widehat':
        return { type: 'accent', body: this.parseGroup(), kind: 'hat' };
      case 'vec':
      case 'overrightarrow':
        return { type: 'accent', body: this.parseGroup(), kind: 'vec' };
      case 'dot':
        return { type: 'accent', body: this.parseGroup(), kind: 'dot' };
      case 'ddot':
        return { type: 'accent', body: this.parseGroup(), kind: 'ddot' };
      case 'dddot':
        return { type: 'accent', body: this.parseGroup(), kind: 'dddot' };
      case 'ddddot':
        return { type: 'accent', body: this.parseGroup(), kind: 'ddddot' };
      case 'tilde':
      case 'widetilde':
        return { type: 'accent', body: this.parseGroup(), kind: 'tilde' };
      case 'bar':
        return { type: 'accent', body: this.parseGroup(), kind: 'bar' };
      case 'acute':
        return { type: 'accent', body: this.parseGroup(), kind: 'acute' };
      case 'grave':
        return { type: 'accent', body: this.parseGroup(), kind: 'grave' };
      case 'check':
        return { type: 'accent', body: this.parseGroup(), kind: 'check' };
      case 'breve':
        return { type: 'accent', body: this.parseGroup(), kind: 'breve' };
      // Display/text style
      case 'displaystyle': {
        const body = this.parseExpr();
        return { type: 'sized', body, sizeMul: 1.0 }; // parsed, displayStyle in context
      }
      case 'textstyle':
      case 'scriptstyle':
      case 'scriptscriptstyle': {
        const mul = cmd === 'textstyle' ? 1 : cmd === 'scriptstyle' ? 0.7 : 0.5;
        return { type: 'sized', body: this.parseGroup(), sizeMul: mul };
      }
      // phantom
      case 'phantom':
        return { type: 'phantom', body: this.parseGroup() };
      // mathbb
      case 'mathbb': {
        this.expect('LBRACE');
        const letter = this.peek().kind === 'CHAR' ? this.consume().value : '?';
        if (this.peek().kind === 'RBRACE') this.consume();
        const ch = MATHBB[letter] ?? letter;
        return { type: 'symbol', ch, atomType: 'ord' };
      }
      case 'mathcal':
      case 'mathscr': {
        this.expect('LBRACE');
        const letter = this.peek().kind === 'CHAR' ? this.consume().value : '?';
        if (this.peek().kind === 'RBRACE') this.consume();
        return { type: 'char', ch: letter, italic: false, atomType: 'ord' };
      }
      // \begin{env} ... \end{env}
      case 'begin':
        return this.parseEnvironment();
      // Spacing commands
      case ',':
        return { type: 'space', em: 3 / 18 };
      case ' ':
        return { type: 'space', em: 4 / 18 };
      case ';':
        return { type: 'space', em: 5 / 18 };
      case ':':
        return { type: 'space', em: 4 / 18 };
      case '!':
        return { type: 'space', em: -3 / 18 };
      case 'quad':
        return { type: 'space', em: 1.0 };
      case 'qquad':
        return { type: 'space', em: 2.0 };
      case 'thinspace':
        return { type: 'space', em: 3 / 18 };
      case 'medspace':
        return { type: 'space', em: 4 / 18 };
      case 'thickspace':
        return { type: 'space', em: 5 / 18 };
      case 'enspace':
        return { type: 'space', em: 0.5 };
      case 'hspace': {
        // \hspace{1em} — simplified: just use 1 em
        this.expect('LBRACE');
        while (this.peek().kind !== 'RBRACE' && this.peek().kind !== 'EOF') this.consume();
        if (this.peek().kind === 'RBRACE') this.consume();
        return { type: 'space', em: 1.0 };
      }
      // Hyphen/dash in math (produce as text)
      case '-':
        return { type: 'text', content: '−' };
      case 'hyp':
        return { type: 'text', content: '‐' };
      // Escaped chars
      case '{':
        return { type: 'symbol', ch: '{', atomType: 'open' };
      case '}':
        return { type: 'symbol', ch: '}', atomType: 'close' };
      case '|':
        return { type: 'symbol', ch: '‖', atomType: 'ord' };
      case '/':
        return { type: 'char', ch: '/', italic: false, atomType: 'ord' };
      case '\\':
        return { type: 'space', em: 0 }; // line break (in arrays)
      // cdot as standalone
      case '.':
        return { type: 'char', ch: '.', italic: false, atomType: 'punct' };
      // \pmod{n} → (mod n) with a medium space before
      case 'pmod': {
        const arg = this.parseGroup();
        return {
          type: 'row',
          items: [
            { type: 'space', em: 4 / 18 },
            { type: 'char', ch: '(', italic: false, atomType: 'open' },
            { type: 'text', content: 'mod' },
            { type: 'space', em: 3 / 18 },
            arg,
            { type: 'char', ch: ')', italic: false, atomType: 'close' },
          ],
        };
      }
      // Cancel / strike-through
      case 'cancel':
        return { type: 'cancel', body: this.parseGroup(), style: 'cancel' };
      case 'bcancel':
        return { type: 'cancel', body: this.parseGroup(), style: 'bcancel' };
      case 'xcancel':
        return { type: 'cancel', body: this.parseGroup(), style: 'xcancel' };
      // Stacking symbols
      case 'stackrel': {
        const over = this.parseGroup();
        const body = this.parseGroup();
        return { type: 'stack', body, over, under: undefined };
      }
      case 'underset': {
        const under = this.parseGroup();
        const body = this.parseGroup();
        return { type: 'stack', body, under, over: undefined };
      }
      case 'overset': {
        const over = this.parseGroup();
        const body = this.parseGroup();
        return { type: 'stack', body, over, under: undefined };
      }
      // Extensible arrows
      case 'xrightarrow': {
        const body = this.parseGroup();
        let over: LatexNode | undefined;
        let under: LatexNode | undefined;
        if (this.peek().kind === 'CARET') {
          this.consume();
          over = this.parseGroup();
        }
        if (this.peek().kind === 'UNDERSCORE') {
          this.consume();
          under = this.parseGroup();
        }
        return {
          type: 'xarrow',
          body: { type: 'drawn', name: 'rightarrow', atomType: 'rel' },
          over,
          under,
          dir: 'right',
        };
      }
      case 'xleftarrow': {
        const body = this.parseGroup();
        let over: LatexNode | undefined;
        let under: LatexNode | undefined;
        if (this.peek().kind === 'CARET') {
          this.consume();
          over = this.parseGroup();
        }
        if (this.peek().kind === 'UNDERSCORE') {
          this.consume();
          under = this.parseGroup();
        }
        return {
          type: 'xarrow',
          body: { type: 'drawn', name: 'leftarrow', atomType: 'rel' },
          over,
          under,
          dir: 'left',
        };
      }
      // Box around expression
      case 'boxed':
        return { type: 'boxed', body: this.parseGroup() };
    }

    // Check drawn symbols
    if (cmd in DRAWN_CMDS) {
      const d = DRAWN_CMDS[cmd];
      return { type: 'drawn', name: d.name, atomType: d.atomType };
    }
    // Check font glyph symbols
    if (cmd in FONT_GLYPHS) {
      const g = FONT_GLYPHS[cmd];
      return { type: 'symbol', ch: g.ch, atomType: g.atomType };
    }

    // Unknown: render as upright text
    return { type: 'text', content: cmd };
  }

  // ---------------------------------------------------------------------------
  // \begin{env} ... \end{env}
  // ---------------------------------------------------------------------------
  private parseEnvironment(): LatexNode {
    this.expect('LBRACE');
    let envName = '';
    while (this.peek().kind !== 'RBRACE' && this.peek().kind !== 'EOF') {
      envName += this.consume().value;
    }
    if (this.peek().kind === 'RBRACE') this.consume();

    const matrixEnvs = new Set([
      'matrix',
      'pmatrix',
      'bmatrix',
      'vmatrix',
      'Vmatrix',
      'Bmatrix',
      'smallmatrix',
      'array',
    ]);

    if (matrixEnvs.has(envName)) {
      return this.parseMatrix(envName);
    }
    // Fallback: parse until \end{envName}
    const body = this.parseExpr();
    if (this.peek().kind === 'COMMAND' && this.peek().value === 'end') {
      this.consume();
      this.expect('LBRACE');
      while (this.peek().kind !== 'RBRACE' && this.peek().kind !== 'EOF') this.consume();
      if (this.peek().kind === 'RBRACE') this.consume();
    }
    return body;
  }

  private parseMatrix(env: string): LatexNode {
    const rows: LatexNode[][] = [];
    let currentRow: LatexNode[] = [];

    while (true) {
      const t = this.peek();
      if (t.kind === 'EOF') break;
      if (t.kind === 'COMMAND' && t.value === 'end') break;
      if (t.kind === 'AMPERSAND') {
        this.consume();
        continue; // cell was already collected by parseExpr stopping at &
      }
      if (t.kind === 'COMMAND' && t.value === '\\') {
        this.consume();
        rows.push(currentRow);
        currentRow = [];
        continue;
      }
      const cell = this.parseExpr();
      currentRow.push(cell);
    }
    // push last row
    if (currentRow.length > 0) {
      rows.push(currentRow);
    }
    // consume \end{env}
    if (this.peek().kind === 'COMMAND' && this.peek().value === 'end') {
      this.consume();
      this.expect('LBRACE');
      while (this.peek().kind !== 'RBRACE' && this.peek().kind !== 'EOF') this.consume();
      if (this.peek().kind === 'RBRACE') this.consume();
    }
    return { type: 'matrix', rows, env };
  }

  private parseDelimChar(): string {
    const t = this.peek();
    if (t.kind === 'LPAR') {
      this.consume();
      return '(';
    }
    if (t.kind === 'RPAR') {
      this.consume();
      return ')';
    }
    if (t.kind === 'LBRACKET') {
      this.consume();
      return '[';
    }
    if (t.kind === 'RBRACKET') {
      this.consume();
      return ']';
    }
    if (t.kind === 'LBRACE') {
      this.consume();
      return '{';
    }
    if (t.kind === 'RBRACE') {
      this.consume();
      return '}';
    }
    if (t.kind === 'CHAR') {
      this.consume();
      return t.value;
    }
    if (t.kind === 'COMMAND') {
      this.consume();
      switch (t.value) {
        case 'lfloor':
          return '⌊';
        case 'rfloor':
          return '⌋';
        case 'lceil':
          return '⌈';
        case 'rceil':
          return '⌉';
        case 'langle':
          return '⟨';
        case 'rangle':
          return '⟩';
        case '|':
          return '‖';
        case '{':
          return '{';
        case '}':
          return '}';
        default:
          return t.value;
      }
    }
    return '.';
  }
}

function mirrorDelim(open: string): string {
  const map: Record<string, string> = {
    '(': ')',
    ')': '(',
    '[': ']',
    ']': '[',
    '{': '}',
    '}': '{',
    '⌊': '⌋',
    '⌋': '⌊',
    '⌈': '⌉',
    '⌉': '⌈',
    '⟨': '⟩',
    '⟩': '⟨',
    '|': '|',
    '‖': '‖',
    '.': '.',
  };
  return map[open] ?? open;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function parseLatex(input: string): LatexNode {
  const tokens = tokenize(input);
  const parser = new Parser(tokens);
  return parser.parseExpr();
}

export { OP_FUNCTIONS };
